/**
 * Kyle Rising — site player bar.
 *
 * A play button anywhere on the site carries its track in data attributes; this
 * picks them up, fills the bar and plays. Tracks without an audio URL still open
 * the bar and show their details, with the transport disabled — the UI is built
 * ahead of the audio, so a missing file degrades to "no sound yet", not an error.
 *
 * State is mirrored to sessionStorage so the bar survives a page navigation:
 * Liquid sites reload on every click, and without this the player would vanish
 * the moment someone opened a song page.
 */
/*
 * Versioned: a saved entry from an older build can describe a track that no
 * longer matches what the page offers — a YouTube-only entry surviving after
 * real audio was added, for instance — so bumping this discards stale state
 * instead of restoring something the site no longer serves.
 */
const STORE_KEY = 'kr-player-v2';

class KRPlayer extends HTMLElement {
  connectedCallback() {
    this.audio = this.querySelector('audio');
    this.bar = this.querySelector('[data-kr-player-bar]');
    this.titleEl = this.querySelector('[data-kr-player-title]');
    this.metaEl = this.querySelector('[data-kr-player-meta]');
    this.coverEl = this.querySelector('[data-kr-player-cover]');
    this.toggleEl = this.querySelector('[data-kr-player-toggle]');
    this.embedEl = this.querySelector('[data-kr-player-embed-slot]');
    this.closeEl = this.querySelector('[data-kr-player-close]');
    this.seekEl = this.querySelector('[data-kr-player-seek]');
    this.linkEl = this.querySelector('[data-kr-player-link]');

    this.prevEl = this.querySelector('[data-kr-player-prev]');
    this.nextEl = this.querySelector('[data-kr-player-next]');
    this.waveEl = this.querySelector('[data-kr-player-wave]');
    this.ytHost = this.querySelector('[data-kr-player-yt-host]');

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-kr-play]');
      if (!trigger) return;
      event.preventDefault();

      // The queue is whatever list the track was played from, in page order —
      // the home carousel, the Music grid — so next/previous follow what the
      // listener can actually see rather than an invented ordering.
      this.#buildQueue(trigger);
      this.#load(trigger.dataset, true);
    });

    this.prevEl?.addEventListener('click', () => this.#step(-1));
    this.nextEl?.addEventListener('click', () => this.#step(1));

    this.toggleEl?.addEventListener('click', () => this.#toggle());
    this.closeEl?.addEventListener('click', () => this.#close());
    this.seekEl?.addEventListener('input', () => this.#seekTo(this.seekEl.value / 100));

    // Clicking the waveform seeks to that point in the track.
    this.waveEl?.addEventListener('kr-seek', (event) => this.#seekTo(event.detail.ratio));

    this.audio?.addEventListener('timeupdate', () => this.#tick());
    this.audio?.addEventListener('play', () => this.#setPlaying(true));
    this.audio?.addEventListener('pause', () => this.#setPlaying(false));
    // A finished track rolls into the next one, as a queue should. At the end of
    // the queue #step does nothing, and the bar simply comes to rest paused.
    this.audio?.addEventListener('ended', () => {
      this.#setPlaying(false);
      this.#step(1);
    });

    this.#restore();
  }

  /**
   * open.spotify.com/track/ID  ->  open.spotify.com/embed/track/ID
   * Accepts album and playlist links too, and spotify:track:ID URIs.
   */
  #spotifyEmbed(url) {
    if (!url) return '';

    const uri = url.match(/^spotify:(track|album|playlist|episode):([A-Za-z0-9]+)/);
    if (uri) return `https://open.spotify.com/embed/${uri[1]}/${uri[2]}`;

    const web = url.match(/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?(track|album|playlist|episode)\/([A-Za-z0-9]+)/);
    return web ? `https://open.spotify.com/embed/${web[1]}/${web[2]}` : '';
  }

  /**
   * youtube.com/watch?v=ID, youtu.be/ID and /shorts/ID all reduce to an embed.
   * nocookie domain: no tracking cookie is set unless someone actually plays.
   */
  #youtubeEmbed(url) {
    if (!url) return '';

    const id =
      url.match(/[?&]v=([A-Za-z0-9_-]{11})/)?.[1] ||
      url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/)?.[1] ||
      url.match(/youtube\.com\/(?:embed|shorts|live)\/([A-Za-z0-9_-]{11})/)?.[1];

    return id ? `https://www.youtube-nocookie.com/embed/${id}` : '';
  }

  #load(data, autoplay, allowEmbed = true) {
    this.dataset.open = 'true';
    this.titleEl.textContent = data.krTitle || '';
    this.metaEl.textContent = data.krMeta || '';

    if (this.coverEl) {
      this.coverEl.innerHTML = data.krCover
        ? `<img src="${data.krCover}" alt="" loading="lazy">`
        : `<span class="kr-player__cover-empty" aria-hidden="true">${(data.krTitle || '').slice(0, 1)}</span>`;
    }

    if (this.linkEl) {
      this.linkEl.href = data.krUrl || '#';
      this.linkEl.hidden = !data.krUrl;
    }

    const hasAudio = Boolean(data.krAudio);

    const peakKey = data.krPeaks || this.#peakKey(data.krAudio);

    if (this.waveEl) {
      // src before key: changing key re-reads the element, and a song with no
      // precomputed peaks needs the audio URL in place to analyse instead.
      this.waveEl.setAttribute('src', data.krAudio || '');
      this.waveEl.setAttribute('key', peakKey);
      this.waveEl.progress = 0;
    }

    // A song page showing its own waveform fills in as the bar plays it, so the
    // two aren't telling the listener different things about the same track.
    this.pageWave = peakKey
      ? document.querySelector(`kr-waveform[key="${peakKey}"]:not([data-kr-player-wave])`)
      : null;
    if (this.pageWave) this.pageWave.progress = 0;

    /*
     * Order matters, and it's a listening decision rather than a technical one:
     *   1. uploaded file — full length, our transport, survives navigation
     *   2. YouTube       — full length for everyone, no account needed
     *   3. Spotify       — 30s unless the visitor is logged in, so it goes last
     *
     * YouTube plays through the hidden host rather than a visible embed: the
     * site is playing a song, not showing a video.
     */
    const ytId = hasAudio ? '' : this.#youtubeId(data.krYoutube);

    let embed = '';
    let source = '';
    if (ytId) {
      source = 'youtube';
    } else if (!hasAudio) {
      embed = this.#spotifyEmbed(data.krSpotify);
      source = embed ? 'spotify' : '';
    }

    // Deferred: restored state shows the bar, but nothing loads or plays until
    // someone presses play — no iframe, and no request to YouTube.
    this.pendingEmbed = embed && !allowEmbed ? embed : '';
    this.pendingYouTube = ytId && !allowEmbed ? ytId : '';

    this.dataset.krPlayerSource = source;
    this.toggleAttribute('data-kr-player-embed', Boolean(embed) && allowEmbed);
    this.toggleAttribute('data-kr-player-silent', !hasAudio && !ytId && !embed);
    this.toggleEl.disabled = !hasAudio && !ytId && !this.pendingEmbed;
    this.seekEl.disabled = !hasAudio && !ytId;

    if (hasAudio) {
      this.audio.pause();
      this.#stopYouTube();
      if (this.audio.getAttribute('src') !== data.krAudio) this.audio.src = data.krAudio;
      this.#clearEmbed();
    } else if (ytId) {
      this.audio.pause();
      this.#clearEmbed();
      if (allowEmbed) this.#playYouTube(ytId, autoplay);
    } else if (embed && allowEmbed) {
      this.audio.pause();
      this.#stopYouTube();
      this.#mountEmbed(embed, autoplay);
    } else {
      this.audio.pause();
      this.#stopYouTube();
      this.#clearEmbed();
    }

    this.#save(data);

    this.#updateSteps();

    if (hasAudio && autoplay) {
      // Autoplay can be refused; the bar stays open either way.
      this.audio.play().catch(() => this.#setPlaying(false));
    }
  }

  #clearEmbed() {
    if (!this.embedEl) return;
    this.embedEl.innerHTML = '';
    delete this.embedEl.dataset.src;
  }

  #mountEmbed(src, autoplay) {
    if (!this.embedEl) return;

    // Rebuilt only when the track changes, so reopening the bar doesn't restart
    // whatever is already playing.
    if (this.embedEl.dataset.src === src) return;

    this.embedEl.dataset.src = src;
    this.embedEl.innerHTML = '';

    const frame = document.createElement('iframe');
    frame.src = `${src}?utm_source=generator${autoplay ? '&autoplay=1' : ''}`;
    frame.width = '100%';
    frame.height = '80';
    frame.frameBorder = '0';
    frame.loading = 'lazy';
    frame.allow = 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture';
    frame.title = 'Spotify player';
    this.embedEl.appendChild(frame);
  }

  /**
   * YouTube audio playback through the IFrame API, driven by the bar's own
   * transport. The player element stays in the layout but off-screen, because
   * browsers throttle or refuse playback in a display:none subtree.
   */
  #youtubeId(url) {
    if (!url) return '';
    return (
      url.match(/[?&]v=([A-Za-z0-9_-]{11})/)?.[1] ||
      url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/)?.[1] ||
      url.match(/youtube\.com\/(?:embed|shorts|live)\/([A-Za-z0-9_-]{11})/)?.[1] ||
      ''
    );
  }

  #loadYouTubeApi() {
    if (window.YT && window.YT.Player) return Promise.resolve();
    if (KRPlayer.apiPromise) return KRPlayer.apiPromise;

    KRPlayer.apiPromise = new Promise((resolve) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previous === 'function') previous();
        resolve();
      };

      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    });

    return KRPlayer.apiPromise;
  }

  async #playYouTube(id, autoplay) {
    await this.#loadYouTubeApi();

    if (this.yt && this.ytId === id) {
      if (autoplay) this.yt.playVideo();
      return;
    }

    if (this.yt) {
      this.ytId = id;
      this.yt.loadVideoById(id);
      if (!autoplay) this.yt.pauseVideo();
      return;
    }

    this.ytId = id;
    this.yt = new window.YT.Player(this.ytHost, {
      videoId: id,
      playerVars: { playsinline: 1, rel: 0, controls: 0 },
      events: {
        onReady: () => {
          if (autoplay) this.yt.playVideo();
        },
        onStateChange: (event) => {
          const state = event.data;
          this.#setPlaying(state === window.YT.PlayerState.PLAYING);

          if (state === window.YT.PlayerState.PLAYING) {
            this.#startYouTubeTicker();
          } else {
            this.#stopYouTubeTicker();
          }

          // Same queue behaviour as an uploaded track: roll into the next song.
          if (state === window.YT.PlayerState.ENDED) this.#step(1);
        },
      },
    });
  }

  #startYouTubeTicker() {
    this.#stopYouTubeTicker();
    this.ytTicker = setInterval(() => {
      if (!this.yt?.getDuration) return;
      const duration = this.yt.getDuration();
      if (!duration) return;
      this.seekEl.value = (this.yt.getCurrentTime() / duration) * 100;
      this.#save();
    }, 500);
  }

  #stopYouTubeTicker() {
    if (this.ytTicker) clearInterval(this.ytTicker);
    this.ytTicker = null;
  }

  /**
   * Paused rather than destroyed: the player instance is reused for the next
   * YouTube track, which loads faster and avoids re-running the API handshake.
   */
  #stopYouTube() {
    this.#stopYouTubeTicker();
    this.pendingYouTube = '';
    try {
      this.yt?.pauseVideo?.();
    } catch {
      /* not ready yet */
    }
  }

  #isYouTube() {
    return this.dataset.krPlayerSource === 'youtube';
  }

  /** The site-wide running order, parsed once. */
  #siteTracks() {
    if (this.tracks) return this.tracks;

    try {
      this.tracks = JSON.parse(this.querySelector('[data-kr-tracks]')?.textContent || '[]');
    } catch {
      this.tracks = [];
    }

    return this.tracks;
  }

  /** Where a track sits in the running order, by page, then file, then title. */
  #indexOf(list, data) {
    const match = (a, b) => a && b && a === b;
    return list.findIndex(
      (t) => match(t.krUrl, data.krUrl) || match(t.krAudio, data.krAudio) || match(t.krTitle, data.krTitle)
    );
  }

  /**
   * Collects every play trigger sharing the clicked one's list, falling back to
   * the whole page when it isn't in a list. Datasets are copied, not the
   * elements, so the queue survives into sessionStorage and across navigation.
   *
   * A song page offers a single play button, which is no queue at all, so the
   * site-wide running order stands in — otherwise previous and next would be
   * dead everywhere except the Music page and the home carousel.
   */
  #buildQueue(trigger) {
    const scope = trigger.closest('ul, ol, .release-grid__list, .track-list') || document;
    const triggers = Array.from(scope.querySelectorAll('[data-kr-play]'));

    if (triggers.length > 1) {
      this.queue = triggers.map((el) => ({ ...el.dataset }));
      this.queueIndex = triggers.indexOf(trigger);
      return;
    }

    const site = this.#siteTracks();
    const index = this.#indexOf(site, trigger.dataset);

    if (site.length > 1 && index > -1) {
      this.queue = site;
      this.queueIndex = index;
      return;
    }

    this.queue = [{ ...trigger.dataset }];
    this.queueIndex = 0;
  }

  /**
   * Wraps in both directions: next from the last track returns to the first,
   * and previous from the first lands on the last, so the discography plays as
   * a loop rather than running into a wall.
   */
  #step(direction) {
    const length = Array.isArray(this.queue) ? this.queue.length : 0;
    if (length === 0) return;

    this.queueIndex = (this.queueIndex + direction + length) % length;
    this.#load(this.queue[this.queueIndex], true);
  }

  #updateSteps() {
    // The queue loops, so position never disables a step — only a queue with
    // nothing to move to does.
    const stuck = (Array.isArray(this.queue) ? this.queue.length : 0) < 2;

    if (this.prevEl) this.prevEl.disabled = stuck;
    if (this.nextEl) this.nextEl.disabled = stuck;
  }

  #toggle() {
    // A restored track reaches out to YouTube on the first press, not before.
    if (this.pendingYouTube) {
      const id = this.pendingYouTube;
      this.pendingYouTube = '';
      this.#playYouTube(id, true);
      return;
    }

    // A restored embed track builds its iframe on the first press, not before.
    if (this.pendingEmbed) {
      this.toggleAttribute('data-kr-player-embed', true);
      this.#mountEmbed(this.pendingEmbed, true);
      this.pendingEmbed = '';
      return;
    }

    if (this.#isYouTube()) {
      if (this.hasAttribute('data-playing')) {
        this.yt?.pauseVideo?.();
      } else {
        this.yt?.playVideo?.();
      }
      return;
    }

    if (this.audio.paused) {
      this.audio.play().catch(() => {});
    } else {
      this.audio.pause();
    }
  }

  /** One seek for both transports, so the slider and the waveform agree. */
  #seekTo(ratio) {
    if (this.#isYouTube()) {
      const duration = this.yt?.getDuration?.();
      if (duration) this.yt.seekTo(ratio * duration, true);
      return;
    }

    if (this.audio?.duration) this.audio.currentTime = ratio * this.audio.duration;
  }

  #close() {
    this.audio?.pause();
    this.#stopYouTube();
    this.removeAttribute('data-open');
    try {
      sessionStorage.removeItem(STORE_KEY);
    } catch {
      /* private mode */
    }
  }

  #setPlaying(playing) {
    this.toggleAttribute('data-playing', playing);
    if (this.toggleEl) {
      this.toggleEl.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    }
  }

  /**
   * Peak tables are keyed by song handle, and the audio asset is named after
   * the same handle — kr-quarters.mp3 — so the key comes free from the URL
   * rather than every play button having to carry it.
   */
  #peakKey(url) {
    return url?.match(/kr-([a-z0-9-]+)\.mp3/i)?.[1] || '';
  }

  #tick() {
    if (!this.audio.duration) return;

    const ratio = this.audio.currentTime / this.audio.duration;
    this.seekEl.value = ratio * 100;
    if (this.waveEl) this.waveEl.progress = ratio;
    if (this.pageWave) this.pageWave.progress = ratio;

    this.#save();
  }

  #save(data) {
    if (data) this.current = { ...data };
    if (!this.current) return;

    try {
      sessionStorage.setItem(
        STORE_KEY,
        JSON.stringify({
          ...this.current,
          time: this.audio?.currentTime || 0,
          queue: this.queue,
          queueIndex: this.queueIndex,
        })
      );
    } catch {
      /* storage unavailable — the bar simply won't survive navigation */
    }
  }

  #restore() {
    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
    } catch {
      return;
    }
    if (!saved) return;

    // Restored, never auto-played: sound starting by itself after a page load
    // is hostile, so the bar returns paused where it left off. Embeds aren't
    // mounted either — an iframe appearing unbidden on every page is noise.
    // The queue is restored with the track, so next/previous still work. A
    // saved queue of one came from a song page under the old behaviour, or
    // from a track the running order doesn't list; either way the site-wide
    // order is the better answer.
    if (Array.isArray(saved.queue) && saved.queue.length > 1) {
      this.queue = saved.queue;
      this.queueIndex = saved.queueIndex ?? 0;
    } else {
      const site = this.#siteTracks();
      const index = this.#indexOf(site, saved);

      if (site.length > 1 && index > -1) {
        this.queue = site;
        this.queueIndex = index;
      }
    }
    
    this.#load(saved, false, false);
    if (saved.time && this.audio && saved.krAudio) {
      this.audio.currentTime = saved.time;

      // The duration isn't known until metadata loads, and preload is off, so
      // the restored position is painted once the browser can place it.
      this.audio.addEventListener('loadedmetadata', () => this.#tick(), { once: true });
    }
  }
}

if (!customElements.get('kr-player')) {
  customElements.define('kr-player', KRPlayer);
}
